import { noForwardCustomProps } from '@apeiron/temp'
import { ApeironTooltip } from '@apeiron/ui'
import { Box, styled } from '@mui/material'
import { SkillElements } from '@src/constants/layout'
import { ColorizeSetting } from '@src/types/layout'
import parse, {
  DOMNode,
  domToReact,
  Element,
  HTMLReactParserOptions,
  Text
} from 'html-react-parser'
import * as R from 'ramda'
import { FC, ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const Paragraph = styled(Box)`
  color: #cccccc;
  display: inline;
  white-space: pre-wrap;
`

const Highlight = styled(
  Box,
  noForwardCustomProps(['color', 'underline', 'weight'])
)<HighlightProps>`
  display: inline-block;
  color: ${props => props.color || 'inherit'};
  text-decoration: ${props => (props.underline ? 'underline' : 'none')};
  font-size: inherit;
  line-height: inherit;
  ${props => `font-weight: ${props.weight || 'inherit'};`}
`

const ExplainableHighlight = styled(Highlight)`
  cursor: pointer;
`

const Explaination = ApeironTooltip

const AdvanceHighlight: FC<AdvanceHighlightProps> = (
  props: AdvanceHighlightProps
) => {
  const { children, setting, checkDictionary } = props

  const searchKey = R.is(String, children) ? (children as string) : ''

  const { t } = useTranslation()

  const explaination = checkDictionary
    ? checkDictionary(searchKey) || t('colorize.no_explaination')
    : t('colorize.no_explaination')

  if (R.isEmpty(searchKey)) {
    return (
      <ExplainableHighlight
        className='Custom-Highlight'
        color={setting.color}
        underline={setting.underline}
        weight={setting.weight}
      >
        {children}
      </ExplainableHighlight>
    )
  }

  return (
    <ExplainableHighlight
      className='Custom-Highlight'
      data-tooltip-id={searchKey}
      color={setting.color}
      underline={setting.underline}
      weight={setting.weight}
    >
      {children}
      <Explaination style={{ backgroundColor: '#696c6e' }} id={searchKey}>
        {explaination}
      </Explaination>
    </ExplainableHighlight>
  )
}

const ColorizedTypography: FC<Props> = (props: Props) => {
  const { className, colorizeMap, text, checkDictionary } = props

  const parseOption: HTMLReactParserOptions = useMemo(() => {
    return {
      replace: (domNode: DOMNode) => {
        if (R.has('name', domNode)) {
          const { attributes, name, children } = domNode as Element

          const setting = R.propOr(
            null,
            name,
            colorizeMap
          ) as ColorizeSetting | null

          if (R.isNotNil(setting)) {
            let component

            if (name === SkillElements.pt) {
              component =
                R.find(attribute => {
                  return attribute.name === 'value'
                }, attributes)?.value || ''

              const suppText = (children?.[0] as Text)?.data

              if (suppText !== undefined) {
                component += suppText
              }
            } else component = domToReact(children, parseOption)

            return setting.hover ? (
              <AdvanceHighlight
                checkDictionary={checkDictionary}
                setting={setting}
              >
                {component}
              </AdvanceHighlight>
            ) : (
              <Highlight
                className='Custom-Highlight'
                color={setting.color}
                underline={setting.underline}
                weight={setting.weight}
              >
                {component}
              </Highlight>
            )
          }

          return null
        }

        return domNode
      }
    }
  }, [colorizeMap, checkDictionary])

  const TextComponents = useMemo(
    () => parse(text, parseOption),
    [parseOption, text]
  )

  return <Paragraph className={className}>{TextComponents}</Paragraph>
}

type AdvanceHighlightProps = {
  children: ReactNode
  setting: ColorizeSetting
  checkDictionary?: (term: string) => string | null
}

type HighlightProps = {
  color?: string
  underline: boolean
  weight: number
}

type Props = {
  className?: string
  colorizeMap: Record<string, ColorizeSetting>
  text: string
  checkDictionary?: (term: string) => string | null
}

export default ColorizedTypography
